import styled from 'styled-components';
import { useTranslations } from 'ilenia';
import React, { useState, useEffect, useRef } from 'react';

import { useClickOutside } from 'hooks';
import useKeypress from 'utilities/keypress';
import { DropDownArrowIcon } from 'components/Icons';

export const DropDownContainer = styled.div`
  ${({ theme, overlay, opened, maxWidth }) => `
    outline: none;
    width: 100%;
    max-width: 544px;
    text-align: left;
    position: relative;
    border-radius: 2px;
    color: ${theme.colors.greyRegular};
    transition: border 0.5s;
    background-color: ${theme.colors.white};
    border: 1px solid ${
      opened ? theme.colors.blueSaturated : theme.colors.greyRegular
    };

    ${maxWidth && `width: calc(100% - ${theme.heights.spacingTablet}px);`};

    ${overlay &&
      `
      position: absolute;
      border: 0;
      z-index: 100;
      padding: 0;
      width: 50%;
      margin: 1px;
      max-width: 78px;

      &:after {
        content: "";
        margin: auto;
        width: 1px;
        height: 50%;
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        border-right: 1px solid;
      }
    `};
  `}
`;

export const DropDownList = styled.ul`
  ${({ theme, opened, overlay, expanded }) => `
    margin: -1px -1px 0;
    padding: 0;
    border-top: 0;
    overflow-y: auto;
    position: absolute;
    list-style-type: none;
    width: calc(100% + 2px);
    background-color: ${theme.colors.white};
    z-index: ${theme.zIndexes.dropdowns};
    border: 1px solid ${theme.colors.blueSaturated};

    ${opened && `border-top: 0`};
    ${overlay && `border-color: ${theme.colors.greyLight}`};
    ${`max-height: ${expanded ? '800' : '300'}px;`};
  `}
`;

export const DropDownSelectedItem = styled.button`
  ${({ theme, error, value }) => `
    display: inline-block;
    padding: 15px 12px 15px 18px;
    color: ${
      error
        ? 'red'
        : value
        ? theme.colors.greyRegular
        : theme.colors.greyRegular
    };
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    width: 100%;
    cursor: pointer;
    text-align: left;
    background: none;
    border: none;

    &:focus {
      outline-radius: 2px;
      outline: 1px solid ${theme.colors.blueSaturated};
    }
  `}
`;

export const DropDownItem = styled.button`
  ${({ theme, empty, selected }) => `
    width: 100%;
    padding: 15px;
    cursor: pointer;
    background: none;
    border: none;
    text-align: left;

    ${empty &&
      `
      cursor: default;
      text-align: center;
      font-style: italic;
    `}

    ${selected ? `background-color: ${theme.colors.greyLighter};` : ''};

    &:focus {
      ${!empty && `background-color: ${theme.colors.greyLighter};`}
      outline: none;
    }
  `}
`;

export const DropDownArrowWrapper = styled.span`
  ${({ theme, opened }) => `
    right: 0;
    padding: 16px 16px 16px 0;
    position: absolute;
    color: ${theme.colors.greyDarkest};

    svg {
      transform: rotate(0deg);
      transition: transform 0.3s ease-in-out;

      ${opened &&
        `
        transform: rotate(-180deg);
      `}
    }
  `}
`;

export const DropDownSearch = styled.input`
  width: 100%;
  border: none;
  background: none;
  padding: 15px 18px;
  display: inline-block;
  transform: translateZ(0);

  :focus {
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.greyDark};
  }
`;

export const FocusedDropDownSearch = props => {
  const ref = useRef();

  useEffect(() => ref.current.focus(), [ref]);

  return <DropDownSearch {...props} ref={ref} />;
};

export const useDropDownLogic = ({ menuItems = [], initialValue }) => {
  const [opened, setOpened] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const dropDownRef = useRef();

  useEffect(() => {
    if (typeof initialValue !== 'undefined') {
      setSelectedItem(menuItems.find(({ value }) => value === initialValue));
    }
  }, []);

  useClickOutside(dropDownRef, () => {
    setOpened(false);
    setSearchTerm('');
  });

  return {
    dropDownRef,
    opened,
    setOpened,
    searchTerm,
    selectedItem,
    setSearchTerm,
    setSelectedItem,
    filteredMenuItems: searchTerm
      ? menuItems.filter(({ label }) =>
          label.toLowerCase().startsWith(searchTerm.toLowerCase())
        )
      : menuItems
  };
};

export const BasicDropDown = ({
  error,
  onChange = () => {},
  menuItems,
  placeholder,
  initialValue,
  disableSearch,
  overlay,
  className = '',
  reset
}) => {
  const [translations] = useTranslations();

  const {
    dropDownRef,
    opened,
    setOpened,
    selectedItem,
    setSearchTerm,
    setSelectedItem,
    filteredMenuItems
  } = useDropDownLogic({
    menuItems,
    initialValue
  });
  const [isReset, setReset] = useState(reset);

  useKeypress('Escape', () => opened && setOpened(false));

  useEffect(() => {
    if (isReset !== reset) {
      setSelectedItem(undefined);
      setReset(reset);
    }
  }, [reset]);

  return (
    <DropDownContainer
      ref={dropDownRef}
      opened={opened}
      className={className}
      overlay={overlay}
      onClick={() => setOpened(!opened)}
    >
      {opened && !disableSearch ? (
        <FocusedDropDownSearch
          onClick={e => e.stopPropagation()}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder={!overlay ? translations['basic/search'] : ''}
        />
      ) : (
        <DropDownSelectedItem
          error={error}
          value={selectedItem ? selectedItem.value : null}
        >
          {error ? error : selectedItem ? selectedItem.label : placeholder}
        </DropDownSelectedItem>
      )}
      <DropDownArrowWrapper opened={opened}>
        <DropDownArrowIcon />
      </DropDownArrowWrapper>
      {opened && (
        <DropDownList opened={opened} overlay={overlay}>
          {filteredMenuItems.length > 0 ? (
            filteredMenuItems.map(item => (
              <li key={`${item.label}-${item.value}`}>
                <DropDownItem
                  onClick={() => {
                    setSelectedItem(item);
                    setSearchTerm('');
                    onChange(item);
                    setOpened(false);
                  }}
                >
                  {item.label}
                </DropDownItem>
              </li>
            ))
          ) : (
            <li>
              <DropDownItem empty>No result found</DropDownItem>
            </li>
          )}
        </DropDownList>
      )}
    </DropDownContainer>
  );
};
